/* ninety.js — behavior for the ninety-top page template sections.
   Self-contained, no dependencies. Initializes every [data-nt-root]
   on the page and re-initializes in the Shopify theme editor.

   Contains two independent IIFEs:
     1) Section behaviors (gallery, reviews, accordion, benefits tabs, options)
     2) Modal controller (.nt-modal dialogs)
*/
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      window.clearTimeout(t);
      t = window.setTimeout(fn, wait);
    };
  }

  /* ---------- Gallery: thumbnails (desktop) + swipe (mobile) + arrows (<980px) ---------- */
  function initGallery(root) {
    var stage = root.querySelector('[data-nt-gallery-stage]');
    var thumbs = root.querySelectorAll('[data-nt-thumb]');
    if (!stage || !thumbs.length) return;

    function setActiveThumb(i) {
      for (var t = 0; t < thumbs.length; t++) {
        thumbs[t].classList.toggle('is-active', t === i);
      }
    }

    for (var i = 0; i < thumbs.length; i++) {
      (function (idx) {
        thumbs[idx].addEventListener('click', function () {
          stage.scrollTo({ left: idx * stage.clientWidth, behavior: 'smooth' });
          setActiveThumb(idx);
        });
      })(i);
    }

    // Prev/next arrows (shown <980px via CSS)
    var gPrev = root.querySelector('[data-nt-gallery-prev]');
    var gNext = root.querySelector('[data-nt-gallery-next]');
    function gCurrent() { return stage.clientWidth ? Math.round(stage.scrollLeft / stage.clientWidth) : 0; }
    if (gPrev) gPrev.addEventListener('click', function () {
      stage.scrollTo({ left: Math.max(0, gCurrent() - 1) * stage.clientWidth, behavior: 'smooth' });
    });
    if (gNext) gNext.addEventListener('click', function () {
      stage.scrollTo({ left: Math.min(thumbs.length - 1, gCurrent() + 1) * stage.clientWidth, behavior: 'smooth' });
    });

    stage.addEventListener('scroll', debounce(function () {
      if (!stage.clientWidth) return;
      setActiveThumb(Math.round(stage.scrollLeft / stage.clientWidth));
    }, 60));
  }

  /* ---------- Reviews: prev/next arrows + dots ---------- */
  function initReviews(root) {
    var track = root.querySelector('[data-nt-reviews-track]');
    if (!track) return;

    var slides = track.querySelectorAll('[data-nt-review]');
    var prev = root.querySelector('[data-nt-reviews-prev]');
    var next = root.querySelector('[data-nt-reviews-next]');
    var dotsWrap = root.querySelector('[data-nt-reviews-dots]');

    function goTo(i) { track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }); }
    function current() { return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0; }

    if (prev) prev.addEventListener('click', function () { goTo(Math.max(0, current() - 1)); });
    if (next) next.addEventListener('click', function () { goTo(Math.min(slides.length - 1, current() + 1)); });

    var dots = [];
    if (dotsWrap && slides.length > 1) {
      // Build dots only once (guard against re-init duplicating them).
      if (!dotsWrap.children.length) {
        for (var d = 0; d < slides.length; d++) {
          (function (idx) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'nt-reviews-dot' + (idx === 0 ? ' is-active' : '');
            dot.setAttribute('aria-label', 'Go to review ' + (idx + 1));
            dot.addEventListener('click', function () { goTo(idx); });
            dotsWrap.appendChild(dot);
          })(d);
        }
      }
      dots = Array.prototype.slice.call(dotsWrap.children);
    }

    track.addEventListener('scroll', debounce(function () {
      var active = current();
      for (var k = 0; k < dots.length; k++) {
        dots[k].classList.toggle('is-active', k === active);
      }
    }, 60));
  }

  function prefersReduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- Accordion: slide animation + one open at a time ----------
     Technique: set an explicit height, force a reflow, then transition to
     the target height. The `transition: height` lives in ninety.css. */

  function closeItem(item) {
    if (!item || !item.open || item._animating) return;
    var panel = item.querySelector('.nt-accordion-panel');
    if (!panel || prefersReduced()) { if (item) item.open = false; return; }

    item._animating = true;
    panel.style.height = panel.scrollHeight + 'px'; // lock current height
    panel.offsetHeight;                             // force reflow
    panel.style.height = '0px';                     // animate to collapsed

    function done(e) {
      if (e.target !== panel || e.propertyName !== 'height') return;
      panel.removeEventListener('transitionend', done);
      item.open = false;        // hide content once fully collapsed
      panel.style.height = '';  // hand height back to CSS
      item._animating = false;
    }
    panel.addEventListener('transitionend', done);
  }

  function openItem(item) {
    if (!item || item._animating) return;
    var panel = item.querySelector('.nt-accordion-panel');
    item.open = true;           // content must be present to measure it
    if (!panel || prefersReduced()) return;

    var end = panel.scrollHeight;
    if (end === 0) return;      // nothing to animate

    item._animating = true;
    panel.style.height = '0px'; // start collapsed
    panel.offsetHeight;         // force reflow
    panel.style.height = end + 'px'; // animate open

    function done(e) {
      if (e.target !== panel || e.propertyName !== 'height') return;
      panel.removeEventListener('transitionend', done);
      panel.style.height = '';  // auto height — stays responsive if content reflows
      item._animating = false;
    }
    panel.addEventListener('transitionend', done);
  }

  function initAccordion(root) {
    var groups = root.querySelectorAll('[data-nt-accordion]');
    for (var g = 0; g < groups.length; g++) {
      (function (group) {
        var items = group.querySelectorAll('.nt-accordion-item');
        for (var i = 0; i < items.length; i++) {
          (function (item) {
            var summary = item.querySelector('.nt-accordion-trigger');
            if (!summary) return;
            summary.addEventListener('click', function (e) {
              e.preventDefault(); // take over the native open/close so we can animate it
              if (item.open) {
                closeItem(item);
              } else {
                for (var k = 0; k < items.length; k++) {
                  if (items[k] !== item) closeItem(items[k]); // single-open
                }
                openItem(item);
              }
            });
          })(items[i]);
        }
      })(groups[g]);
    }
  }

  /* ---------- Benefits: pill tabs -> content panel ---------- */
  function initBenefits(root) {
    var pillsWrap = root.querySelector('[data-nt-benefits-pills]');
    if (!pillsWrap) return;

    var pills = root.querySelectorAll('[data-nt-tab]');
    var panels = root.querySelectorAll('[data-nt-panel]');

    function activate(i) {
      for (var p = 0; p < pills.length; p++) pills[p].classList.toggle('is-active', p === i);
      for (var r = 0; r < panels.length; r++) panels[r].classList.toggle('is-active', r === i);
    }

    for (var i = 0; i < pills.length; i++) {
      (function (idx) {
        pills[idx].addEventListener('click', function () {
          activate(idx);
          // keep the active pill in view on the mobile scroll row
          pills[idx].scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        });
      })(i);
    }
  }

  /* ---------- Purchase options: active state + Add To Cart link + sticky drop bar ----------
     Clicking an option makes it active, rewrites the Add To Cart href, and
     mirrors the active option (name, prices, link) into the fixed drop bar.
     The drop bar slides in once the main Add To Cart scrolls out the top. */
  function initOptions(root) {
    var wrap = root.querySelector('[data-nt-options]');
    if (!wrap) return;

    var opts = wrap.querySelectorAll('[data-nt-option]');
    if (!opts.length) return;

    var atc = root.querySelector('[data-nt-atc]');

    // Per-option detail panels (present only on the subs variant; empty elsewhere)
    var details = root.querySelectorAll('[data-nt-detail]');

    // Sticky drop bar (may live anywhere; look it up by id)
    var drop = document.getElementById('nav-drop-new');
    var dropName = drop && drop.querySelector('#nav-drop-pro-name');
    var dropReal = drop && drop.querySelector('#price-real');
    var dropStrike = drop && drop.querySelector('#price-strike');
    var dropCta = drop && drop.querySelector('.drop-cta');

    function buildUrl(opt) {
      // Custom URL override
      if (opt.getAttribute('data-nt-use-url') === 'true') {
        return opt.getAttribute('data-nt-url') || '#';
      }

      var variant = (opt.getAttribute('data-nt-product-id') || '').trim();
      if (!variant) return '#';
      var qty = (opt.getAttribute('data-nt-qty') || '1').trim() || '1';
      var plan = (opt.getAttribute('data-nt-selling-plan') || '').trim();
      var code = (opt.getAttribute('data-nt-discount-code') || '').trim();

      // SUBSCRIPTION (has a selling plan):
      // Proven format — clear, then add via items[][...], then checkout.
      // ONLY the & separators are encoded (%26) so the nested params stay
      // attached to /cart/add instead of Shopify re-parsing them.
      if (plan) {
        var amp = '%26';
        var chain = '/cart/add?items[][id]=' + variant
          + amp + 'items[][quantity]=' + qty
          + amp + 'items[][selling_plan]=' + plan
          + amp + 'return_to=/checkout';
        if (code) chain += '?discount=' + code;
        return '/cart/clear?return_to=' + chain;
      }

      // ONE-TIME purchase: compact permalink -> checkout (supports discount)
      var url = '/cart/' + encodeURIComponent(variant) + ':' + encodeURIComponent(qty);
      if (code) url += '?discount=' + encodeURIComponent(code);
      return url;
    }

    function updateDrop(opt) {
      if (!drop) return;
      if (dropName) dropName.textContent = opt.getAttribute('data-nt-name') || '';
      if (dropReal) dropReal.textContent = opt.getAttribute('data-nt-price-real') || '';
      if (dropStrike) dropStrike.textContent = opt.getAttribute('data-nt-price-list') || '';
      if (dropCta) dropCta.setAttribute('href', buildUrl(opt));
    }

    function activate(opt) {
      var idx = -1;
      for (var i = 0; i < opts.length; i++) {
        opts[i].classList.toggle('active', opts[i] === opt);
        if (opts[i] === opt) idx = i;
      }
      for (var d = 0; d < details.length; d++) {
        details[d].classList.toggle('is-active', d === idx);
      }
      if (atc) atc.setAttribute('href', buildUrl(opt));
      updateDrop(opt);
    }

    for (var i = 0; i < opts.length; i++) {
      (function (opt) {
        opt.addEventListener('click', function () { activate(opt); });
      })(opts[i]);
    }

    // Initialize from the pre-active option (or the first one)
    var initial = wrap.querySelector('[data-nt-option].active') || opts[0];
    activate(initial);

    // Slide the drop bar in once the main Add To Cart has scrolled out the top
    var atcRef = root.querySelector('.nt-add-to-cart');
    if (drop && atcRef && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var e = entries[0];
        // visible only when the reference has scrolled ABOVE the viewport top
        var past = !e.isIntersecting && e.boundingClientRect.top < 0;
        drop.classList.toggle('is-visible', past);
      }, { threshold: 0 });
      io.observe(atcRef);
    }
  }

  /* ---------- Init ---------- */
  function init(root) {
    if (!root || root.dataset.ntInit === 'true') return; // idempotent
    root.dataset.ntInit = 'true';
    initGallery(root);
    initReviews(root);
    initAccordion(root);
    initBenefits(root);
    initOptions(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-nt-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: a fresh copy of the section is injected on edit.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();

/* modal.js — controller for .nt-modal dialogs.
   Handles any number of modals via event delegation, so modals
   added later (even injected by other scripts) just work.

   Open:  <button data-nt-modal-open="MODAL_ID">Open</button>
   Close: <button data-nt-modal-close>Close</button>  (any element inside the modal)
          click on the backdrop, or press Esc (native).
*/
(function () {
  'use strict';

  var openCount = 0;

  function lockScroll() {
    if (openCount === 0) document.documentElement.style.overflow = 'hidden';
    openCount++;
  }
  function unlockScroll() {
    openCount = Math.max(0, openCount - 1);
    if (openCount === 0) document.documentElement.style.overflow = '';
  }

  function openModal(dialog) {
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
    dialog.showModal();
    lockScroll();
  }

  function closeModal(dialog) {
    if (dialog && dialog.open) dialog.close(); // triggers the CSS exit transition
  }

  document.addEventListener('click', function (e) {
    // Open trigger
    var opener = e.target.closest('[data-nt-modal-open]');
    if (opener) {
      e.preventDefault();
      openModal(document.getElementById(opener.getAttribute('data-nt-modal-open')));
      return;
    }

    // Close trigger
    var closer = e.target.closest('[data-nt-modal-close]');
    if (closer) {
      e.preventDefault();
      closeModal(closer.closest('.nt-modal'));
      return;
    }

    // Backdrop click: target is the dialog itself (content lives in .nt-modal-inner,
    // and the dialog has padding:0, so a click on the dialog node = the backdrop).
    if (e.target.classList && e.target.classList.contains('nt-modal')) {
      closeModal(e.target);
    }
  });

  // Keep the scroll lock correct no matter how the dialog closed
  // (button, backdrop, or Esc). 'close' doesn't bubble, so listen in capture.
  document.addEventListener('close', function (e) {
    if (e.target.classList && e.target.classList.contains('nt-modal')) {
      unlockScroll();
    }
  }, true);
})();